import { useEffect, useReducer, useRef } from "react";
import { STORAGE_KEYS } from "../utils/constants";

// Live progress of one agent run, fed by the SSE endpoint /stream/agent/<execution_id>
// (an enriched `typing` WS frame carries the URL — see Chat.js). Each SSE data line is
// one JSON progress event written by the agent worker:
//   {seq, t, step: start|plan|tool|check|done, label, detail, state, round, steps?}
// The stream replays the whole file on connect, so a refreshed browser reconstructs the
// timeline; it ends with the <<END_STREAM>> sentinel. The reducer folds events into one
// renderable state — Chat.js stays dumb about agent internals.

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 1000;
const SENTINEL = "<<END_STREAM>>";
const MAX_TOOL_CHIPS = 12; // kept per plan-step burst; display caps further

export const initialAgentProgress = {
  active: false,      // a stream is (or was) attached for the current run
  finished: false,    // sentinel or terminal event seen — hide the live line
  failed: false,
  stalled: false,     // connection lost and retries exhausted (run may still finish)
  label: "",
  detail: "",
  phase: "start",     // start | plan | tool | check | done
  state: "running",
  round: 1,
  steps: [],          // the plan checklist: [{label, state}]
  tools: [],          // recent activity chips: [{label, detail, state}]
  methodLabel: "",    // "Using our Share-loss playbook — 5 steps"
  checksDetail: "",   // "figures match the data · totals add up · …"; after `done`, the final summary
  finalState: "",     // how the run ended, from the done event: done | warn | failed ("" until it lands)
  startedAt: null,    // epoch seconds of the first event
  finishedAt: null,
  lastSeq: -1,
};

function reduce(current, action) {
  if (action.type === "reset") {
    // Dropping the URL (action.active false) after a run finished must NOT wipe the
    // state: the method chip's expandable timeline renders from it after the stream
    // is released. A new run's URL (action.active true) always starts clean.
    if (!action.active && current.finished) return current;
    return { ...initialAgentProgress, active: !!action.active };
  }
  if (action.type === "stalled") {
    return { ...current, stalled: true };
  }
  if (action.type === "sentinel") {
    return { ...current, finished: true, finishedAt: current.finishedAt || Date.now() / 1000 };
  }
  if (action.type !== "event") return current;

  const e = action.event || {};
  const next = {
    ...current,
    active: true,
    label: e.label || current.label,
    detail: e.detail !== undefined ? e.detail : current.detail,
    phase: e.step || current.phase,
    state: e.state || current.state,
    round: e.round || current.round,
    startedAt: current.startedAt ?? e.t ?? null,
    lastSeq: typeof e.seq === "number" ? e.seq : current.lastSeq,
  };

  switch (e.step) {
    case "plan": {
      if (Array.isArray(e.steps) && e.steps.length > 0) next.steps = e.steps;
      if ((e.label || "").startsWith("Using our")) next.methodLabel = e.label;
      next.tools = []; // a plan update starts a fresh activity burst under the new step
      return next;
    }
    case "tool": {
      const tools = [...current.tools];
      const openIndex = tools.findIndex(
        (t) => t.label === e.label && t.state === "running"
      );
      if (e.state === "done" && openIndex > -1) {
        tools[openIndex] = { ...tools[openIndex], state: "done" };
      } else if (e.state !== "done") {
        tools.push({ label: e.label, detail: e.detail || "", state: "running" });
      }
      next.tools = tools.slice(-MAX_TOOL_CHIPS);
      return next;
    }
    case "check": {
      // The row shows the "·"-joined list of what has passed; a bare stage line (no "·"
      // yet — nothing has passed) is not promoted to it.
      if (e.detail && e.detail.includes("·")) next.checksDetail = e.detail;
      return next;
    }
    case "done": {
      // The terminal event says how the run ended — done, warn or failed — and its detail
      // is the honest final summary ("figures checked against the data · fit to the
      // question not confirmed"), which replaces whatever the draft-time check line said.
      next.finished = true;
      next.finalState = e.state || "done";
      next.failed = e.state === "failed";
      next.finishedAt = e.t || Date.now() / 1000;
      if (e.detail) next.checksDetail = e.detail;
      return next;
    }
    default:
      return next;
  }
}

export default function useAgentProgress(streamUrl) {
  const [progress, dispatch] = useReducer(reduce, initialAgentProgress);
  const sourceRef = useRef(null);
  const retryRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "reset", active: !!streamUrl });
    if (!streamUrl) return undefined;

    let disposed = false;

    let token = null;
    try {
      token = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION))?.token;
    } catch (e) { /* localStorage unavailable — connect without token */ }

    let urlWithToken = streamUrl;
    if (token) {
      try {
        const urlObj = new URL(streamUrl, window.location.origin);
        urlObj.searchParams.set("token", token);
        urlWithToken = urlObj.toString();
      } catch (e) {
        urlWithToken = `${streamUrl}${streamUrl.indexOf("?") === -1 ? "?" : "&"}token=${encodeURIComponent(token)}`;
      }
    }

    const connect = () => {
      if (disposed) return;
      if (sourceRef.current) {
        try { sourceRef.current.close(); } catch (e) { /* already closed */ }
      }
      const source = new EventSource(urlWithToken);
      sourceRef.current = source;

      source.onmessage = (event) => {
        if (!event.data) return;
        if (event.data.indexOf(SENTINEL) !== -1) {
          dispatch({ type: "sentinel" });
          source.close();
          sourceRef.current = null;
          return;
        }
        try {
          const parsed = JSON.parse(event.data);
          retryRef.current = 0;
          dispatch({ type: "event", event: parsed });
        } catch (e) {
          console.error("Unparseable agent progress line", e, event.data);
        }
      };

      source.onerror = () => {
        source.close();
        sourceRef.current = null;
        if (disposed) return;
        if (retryRef.current < MAX_RETRIES) {
          const delay = RETRY_BASE_DELAY * Math.pow(2, retryRef.current);
          retryRef.current += 1;
          retryTimerRef.current = setTimeout(connect, delay);
        } else {
          // The display lost its feed; the run itself is unaffected and the final
          // answer still arrives over the chat WebSocket.
          dispatch({ type: "stalled" });
        }
      };
    };

    retryRef.current = 0;
    connect();

    return () => {
      disposed = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (sourceRef.current) {
        try { sourceRef.current.close(); } catch (e) { /* already closed */ }
        sourceRef.current = null;
      }
    };
  }, [streamUrl]);

  return progress;
}
